import { FC, useState } from 'react'
import {
    Container,
    CasesList,
    CaseItem,
    CaseType,
    CaseTitle,
    CaseMetadata,
    StatusTag,
    EmptyStateContainer,
    EmptyStateTitle,
    EmptyStateSubtitle,
    CustomTabsWrapper,
} from './OpenCases.styles'
import { statusTextMap, getStatusIcon } from './OpenCases.utils'
import {
    SectionTitle,
    SectionHeader,
    Card as StyledCard,
    SectionHeaderContent,
} from '../../overview.styles'
import { MdrModule } from '@/core/modules/mdr/mdr.module'
import { useMdrCasesQuery } from '@core/hooks/useMdrCasesQuery'
import ThreatResolutionIcon from '@assets/images/threat-resolution.svg?react'
import { TabType, useOpenCases } from './useOpenCases/useOpenCases'
import { useCustomTheme } from '@/core/theme'
import { CustomTabs } from '@atbay/cyber-components'
import { Analytics } from '@/core/services/mixpanel'
import { AccountModule } from '@/core/modules/account/account.module'

export const OpenCases: FC = () => {
    const { isMdrActive } = AccountModule.useStore()
    const timeRange = MdrModule.getTimeRange()
    const [activeTab, setActiveTab] = useState<TabType>('all')
    const { data } = useMdrCasesQuery()
    const allCases = data
        ? MdrModule.processCases(data, timeRange)
        : { incidents: [], alerts: [], serviceRequests: [] }
    const theme = useCustomTheme()
    const { incidents = [], serviceRequests = [] } = allCases
    const { filteredCases, tabs, openDrawer } = useOpenCases(
        { incidents, serviceRequests },
        timeRange,
        activeTab
    )

    const tabItems = tabs.map((tab) => ({
        key: tab.id,
        label: (
            <span data-testid={`open-cases-${tab.id}-tab`}>
                <span className="tab-label">{tab.label}</span>
                <span className="tab-count">({tab.count})</span>
            </span>
        ),
    }))

    const handleCaseClick = (
        type: 'Incident' | 'ServiceRequest',
        id: string,
        title: string,
        severity: TCaseSeverity,
        status: string,
        lastModified: string
    ) => {
        Analytics.event(
            type === 'Incident'
                ? 'SEM::mdr_incident_details_drawer_open'
                : 'SEM::mdr_service_request_details_drawer_open',
            {
                isMdrActive,
                origin: 'mdr_overview_open_cases',
            }
        )

        openDrawer({
            id,
            type,
            title,
            severity,
            status,
            lastModified,
        })
    }

    return (
        <Container>
            <SectionHeader>
                <SectionHeaderContent>
                    <SectionTitle variant="m-semibold" component="h2">
                        Open Cases
                    </SectionTitle>
                </SectionHeaderContent>
            </SectionHeader>
            <StyledCard className="open-cases-card">
                <CustomTabsWrapper>
                    <CustomTabs
                        items={tabItems}
                        activeKey={activeTab}
                        onTabChange={(key) => setActiveTab(key as TabType)}
                        activeStyles={{ color: theme.colors.greys[800] }}
                        inactiveStyles={{ color: theme.colors.greys[600] }}
                    />
                </CustomTabsWrapper>
                {filteredCases.length === 0 ? (
                    <EmptyStateContainer>
                        <ThreatResolutionIcon width={64} height={64} />
                        <EmptyStateTitle variant="xl-regular">
                            You're all set!
                        </EmptyStateTitle>
                        <EmptyStateSubtitle variant="s-regular">
                            There are no incidents or service requests waiting
                            for your attention.
                        </EmptyStateSubtitle>
                    </EmptyStateContainer>
                ) : (
                    <CasesList data-testid="open-cases-scroll-container">
                        {filteredCases.map(
                            ({
                                id,
                                type,
                                title,
                                severity,
                                status,
                                lastModified,
                            }) => (
                                <CaseItem
                                    key={id}
                                    data-testid={`case-item-${id}`}
                                    severity={severity}
                                    type={type}
                                    onClick={() =>
                                        handleCaseClick(
                                            type,
                                            id,
                                            title,
                                            severity,
                                            status,
                                            lastModified
                                        )
                                    }
                                >
                                    <CaseType
                                        type={type}
                                        data-testid={`case-type-${type.toLowerCase()}`}
                                    >
                                        {type === 'Incident'
                                            ? 'Incident'
                                            : 'Service Request'}
                                    </CaseType>
                                    <CaseTitle
                                        variant="m-regular"
                                        testId={`case-title-${title}`}
                                    >
                                        {title}
                                    </CaseTitle>
                                    <CaseMetadata>
                                        <StatusTag
                                            status={status as any}
                                            data-testid={`case-status-${status.toLowerCase()}`}
                                        >
                                            {type === 'Incident' && (
                                                <>
                                                    {getStatusIcon(severity)}
                                                    <span
                                                        className="severity-text capitalize"
                                                        data-testid={`case-severity-${severity.toLowerCase()}`}
                                                    >
                                                        {severity}
                                                    </span>
                                                </>
                                            )}
                                            <span>
                                                {statusTextMap[status] ||
                                                    status}
                                            </span>
                                        </StatusTag>
                                        <span
                                            className="created-date"
                                            data-testid={`case-created-date-${lastModified}`}
                                        >
                                            Created Date: {lastModified}
                                        </span>
                                    </CaseMetadata>
                                </CaseItem>
                            )
                        )}
                    </CasesList>
                )}
            </StyledCard>
        </Container>
    )
}
